import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, map } from "rxjs";
import { LocalStorageService } from "src/app/_services/localStorage.service";
import { environment } from "src/environments/environment";
import {
  ConversationObject
} from "../../interfaces/conversation.interface";

@Injectable({
  providedIn: "root",
})
export class MessagesService {
  url = `${environment.apiUrl}`;
  authUrl = `${environment.authUrl}`;
  realtimeClientMessage: Subject<any> = new Subject<any>();
  public _message: any = new BehaviorSubject(0);
  public _conversations: BehaviorSubject<ConversationObject[]> =
    new BehaviorSubject<ConversationObject[]>([]);

  public _unreadConversations: BehaviorSubject<any[]> = new BehaviorSubject<
    any[]
  >([]);
  private conversationListUpdated$: Subject<any> = new Subject<any>();

  constructor(
    private http: HttpClient,
    private localStorgaeService: LocalStorageService
  ) { }

  public getConversations(): Observable<any> {
    return this._conversations.asObservable();
  }
  setConversations(data: any) {
    this._conversations.next(data);
  }
  addConversations(data: any) {
    let unreadedcount = 0;
    const sender_id = data.sender.id;
    const reciever_id = this.localStorgaeService.getUserData().client.id;
    const dataToSave: ConversationObject = {
      user_id: sender_id,
      list: [
        {
          id: data.id || 0,
          isRead: data.is_read || 0,
          message: data.message || "",
          updated_at: data.updated_at || "",
          created_at: data.created_at || "",
          sender: data.sender || 0,
          reciever: data.reciever || 0,
          type: data.type || "text",
          duration: data.duration || null,
          user1_id: sender_id || 0,
          user2_id: reciever_id || 0,
        },
      ],
    };

    const currentData = this._conversations.getValue();
    const senderConvIndex = currentData.findIndex(
      (convData: ConversationObject) => {
        return convData.user_id === sender_id;
      }
    );
    let updatedData;
    if (senderConvIndex >= 0) {
      const senderConv: ConversationObject = currentData[senderConvIndex];

      senderConv.list = [...senderConv.list, ...dataToSave.list];
      // Compter seulement les messages non lus (isRead === 0)
      unreadedcount = senderConv.list.filter((msg: any) => this.isUnreadMessage(msg)).length;
      const slice1 = currentData.slice(0, senderConvIndex)
      const slice2 = currentData.slice(senderConvIndex + 1)
      updatedData = [
        ...slice1,
        senderConv,
        ...slice2,
      ];
    } else {
      updatedData = [...currentData, dataToSave];
      // Si c'est une nouvelle conversation et le message n'est pas lu, le compteur est 1
      if (data.is_read === 0) {
        unreadedcount = 1;
      }
    }
    this._conversations.next(updatedData);

    // Mettre à jour aussi les conversations non lues si le message n'est pas lu
    if (data.is_read === 0) {
      const unreadData = {
        message: data.message,
        updated_at: new Date(),
        is_read: data.is_read,
        sender: data.sender,
        other: data.sender, // L'expéditeur est "l'autre" pour le destinataire
        unread_count: unreadedcount
      };
      this.updateUnreadConversation(unreadData);
    }
  }

  /** sender / other peuvent être un utilisateur { id } ou un id brut (API). */
  resolveConversationUserRef(ref: any): number | null {
    if (ref == null) return null;
    if (typeof ref === "object" && "id" in ref) {
      const n = Number(ref.id);
      return Number.isNaN(n) ? null : n;
    }
    const n = Number(ref);
    return Number.isNaN(n) ? null : n;
  }

  unreadEntryInvolvesPeer(entry: any, peerUserId: number): boolean {
    const p = Number(peerUserId);
    return (
      this.resolveConversationUserRef(entry?.sender) === p ||
      this.resolveConversationUserRef(entry?.other) === p
    );
  }

  private isUnreadMessage(message: any): boolean {
    const readStatus = message?.isRead ?? message?.is_read ?? 0;
    return Number(readStatus) === 0;
  }

  readUserConversations(peerUserId: number) {
    const currentData = this._conversations.getValue();
    const idx = currentData.findIndex(
      (convData: ConversationObject) =>
        Number(convData.user_id) === Number(peerUserId)
    );
    if (idx >= 0) {
      const conv = currentData[idx];
      const updatedList = conv.list.map((msg: any) => ({ ...msg, isRead: 1, is_read: 1 }));
      const updatedData = [
        ...currentData.slice(0, idx),
        { ...conv, list: updatedList },
        ...currentData.slice(idx + 1),
      ];
      this._conversations.next(updatedData);
    }

    const peerId = Number(peerUserId);
    const unread = this._unreadConversations.getValue();
    const nextUnread = unread.filter(
      (item: any) => !this.unreadEntryInvolvesPeer(item, peerId)
    );
    this._unreadConversations.next(nextUnread);
  }

  public getRealtimeMessages(): Observable<any> {
    return this.realtimeClientMessage.asObservable();
  }

  public getUnreadConversations(): Observable<any> {
    return this._unreadConversations.asObservable();
  }
  getConversationListUpdates(): Observable<any> {
    return this.conversationListUpdated$.asObservable();
  }
  notifyConversationListUpdated(payload?: any): void {
    this.conversationListUpdated$.next(payload ?? null);
  }
  setUnreadConversation(data: any[]) {
    this._unreadConversations.next(data);
  }
  addUnreadConversation(data: any) {
    let oldArray = this._unreadConversations.getValue();
    oldArray.push(data);
    this._unreadConversations.next(oldArray);
  }

  updateUnreadConversation(data: any) {
    let oldArray = this._unreadConversations.getValue();
    const senderId = data.sender.id;
    // Chercher si une conversation existe déjà avec cet expéditeur
    const existingIndex = oldArray.findIndex((conv: any) => {
      return conv.sender.id === senderId || conv.other.id === senderId;
    });

    if (existingIndex >= 0) {
      // Récupérer le compteur existant
      const previousCount = oldArray[existingIndex].unread_count || 0;

      // Calculer le vrai nombre de messages non lus depuis _conversations
      const currentConversations = this._conversations.getValue();
      const conversation = currentConversations.find((conv: ConversationObject) => {
        return conv.user_id === senderId;
      });

      // Compter les messages non lus dans cette conversation
      let calculatedCount = conversation
        ? conversation.list.filter((msg: any) => this.isUnreadMessage(msg)).length
        : previousCount + 1; // Si pas de conversation trouvée, incrémenter

      // Utiliser le maximum entre le compteur calculé et l'ancien + 1
      // Cela évite les réinitialisations à 0 quand _conversations n'est pas complètement synchronisé
      const actualUnreadCount = Math.max(calculatedCount, previousCount + 1);

      // Mettre à jour la conversation existante avec le vrai compteur
      oldArray[existingIndex] = {
        ...oldArray[existingIndex],
        message: data.message,
        updated_at: data.updated_at,
        is_read: data.is_read,
        unread_count: actualUnreadCount
      };
    } else {
      // Ajouter une nouvelle conversation
      oldArray.push(data);
    }

    this._unreadConversations.next(oldArray);
  }

  initUnreadConversation() {
    let userId = this.localStorgaeService.getUserData().client.id;
    this.getAllConversations()
      .pipe(
        map((res: any) => {
          return res.discussions.map((discussion: any) => ({
            message: discussion.message,
            updated_at: discussion.updated_at,
            is_read: discussion.is_read,
            reciever: discussion.user1_id,
            sender: discussion.user2_id,
            unread_count: discussion.unread_count,

            other:
              discussion.user2_id.id != userId
                ? discussion.user2_id
                : discussion.user1_id,
          }));
        })
      )
      .subscribe((userConversations: any[]) => {
        this.setUnreadConversation([]);
        userConversations.map((discussion: any) => {
          discussion.is_read === 0 &&
            discussion.reciever.id != userId &&
            this.addUnreadConversation(discussion);
        });
      });
  }
  removeUnreadConversation(data: any) {
    let oldArray = this._unreadConversations.getValue(); // Retrieve the current array
    let newArray = oldArray.filter((item) => item !== data); // Remove dataToRemove from the array
    this._unreadConversations.next(newArray); // Emit the updated array
  }
  setUnreadMessages() {
    this._message.next(this._message.value + 1);
  }
  getUnreadMessages(): Observable<any> {
    return this._message.asObservable();
  }
  clearUnreadMessages() {
    this._message.next(0);
  }

  /*==============================
send message
================================*/
  sendMessage(
    userId: string,
    payload: { message: string; voiceMessage: string; duration: number }
  ) {
    const data =
      payload.message && payload.message.length
        ? {
          user_id: userId,
          message: payload.message,
          type: "text",
        }
        : {
          user_id: userId,
          file: payload.voiceMessage,
          duration: payload.duration,
          type: "audio",
        };
    return this.http.post(`${this.authUrl}/frontoffice/send-message`, data);
  }
  /*==============================
get message
================================*/
  getMessage(userId: string) {
    const data = {
      user_id: userId,
    };
    return this.http.post(`${this.authUrl}/frontoffice/get-messages`, data);
  }
  /*==============================
get all conversations 
================================*/
  getAllConversations(search?: string) {
    let payload = search
      ? {
        search: search,
      }
      : {};
    return this.http.post(
      `${this.authUrl}/frontoffice/get-all-messages`,
      payload
    );
  }
  /*==============================
search conversations search
================================*/
  searchConversations() {
    return this.http.get(`${this.authUrl}/frontoffice/get-all-messages`);
  }
  /*==============================
send messages to admin
================================*/
  sendAdminMessage(payload: any) {
    const data =
      payload.message && payload.message.length
        ? {
          message: payload.message,
          type: "text",
        }
        : {
          file: payload.voiceMessage,
          duration: payload.duration,
          type: "audio",
        };
    return this.http.post(`${this.authUrl}/backoffice/send-message`, data);
  }
  /*==============================
get admin messages
================================*/
  getAdminMessages() {
    return this.http.get(`${this.authUrl}/backoffice/message-admins`);
  }

  /*==============================
user admin messages
================================*/
  getUnreadMessagesApi() {
    return this.http.get(`${this.authUrl}/frontoffice/unread-messages`);
  }
  fetchUnreadMessagesApi() {
    const id = this.localStorgaeService.getUserData().client.id;
    this.getUnreadMessagesApi().subscribe((response: any) => {
      if (!response || !response.length) {
        this.setUnreadConversation([]);
        return;
      }
      const data: ConversationObject[] = [];
      const unreadConversationsData: any[] = [];

      response.forEach((element: any) => {
        if (element.user_id != id) {
          let group: ConversationObject = {
            user_id: element.user_id,
            list: element.messagesList || [],
          };
          data.push(group);

          const unreadCount = group.list.filter((msg: any) => this.isUnreadMessage(msg)).length;
          const unreadMessages = group.list.filter((msg: any) => this.isUnreadMessage(msg));
          const lastUnreadMessage =
            unreadMessages.length > 0
              ? unreadMessages[unreadMessages.length - 1]
              : null;

          if (unreadCount > 0 && lastUnreadMessage) {
            unreadConversationsData.push({
              message: lastUnreadMessage.message || "",
              updated_at: lastUnreadMessage.updated_at || new Date(),
              is_read: 0,
              sender: lastUnreadMessage.sender || element.user_id,
              other: element.user_id,
              unread_count: unreadCount,
            });
          }
        }
      });

      const existingConversations = this._conversations.getValue();
      const mergedConversations = this.mergeConversations(
        existingConversations,
        data
      );
      this.setConversations(mergedConversations);

      // Source de vérité API : remplacer les non-lus (évite de garder un badge obsolète)
      this.setUnreadConversation(unreadConversationsData);
    });
  }

  // Fusionner les conversations existantes avec les nouvelles (évite de perdre les messages déjà chargés)
  private mergeConversations(existing: ConversationObject[], newData: ConversationObject[]): ConversationObject[] {
    const merged = [...existing];

    newData.forEach((newConv: ConversationObject) => {
      const existingIndex = merged.findIndex((conv: ConversationObject) => conv.user_id === newConv.user_id);

      if (existingIndex >= 0) {
        // Fusionner les listes de messages en évitant les doublons
        const existingConv = merged[existingIndex];
        const existingMessageIds = new Set(existingConv.list.map((msg: any) => msg.id));

        // Ajouter seulement les nouveaux messages qui n'existent pas déjà
        const newMessages = newConv.list.filter((msg: any) => !existingMessageIds.has(msg.id));
        merged[existingIndex] = {
          ...existingConv,
          list: [...existingConv.list, ...newMessages]
        };
      } else {
        // Nouvelle conversation, l'ajouter
        merged.push(newConv);
      }
    });

    return merged;
  }
}
